import { queryOptions } from '@tanstack/react-query'

import type { OrderingContext } from '@/bindings/OrderingContext'
import type { PlaceOrderRequest } from '@/bindings/PlaceOrderRequest'
import type { MyOrdersResponse } from '@/bindings/MyOrdersResponse'
import type { PlacedOrder } from '@/bindings/PlacedOrder'
import type { TrackedOrder } from '@/bindings/TrackedOrder'
import type { TrackedSession } from '@/bindings/TrackedSession'
import { request } from '@/lib/api'
import { PAGE_SIZE } from '@/lib/paging'

/** Mirror of `api/tables.rs::{TAKE_AWAY_SEGMENT, TABLE_SEGMENT}`: printed QRs carry these (G3). */
export const TAKE_AWAY_SEGMENT = 'order'
export const TABLE_SEGMENT = 't'
/** The order and session tracking leaves (URL scheme 2026-09-01, G7). */
export const ORDER_SEGMENT = 'o'
export const SESSION_SEGMENT = 's'

/** The poll fallback behind G5's hint stream (T7); the stream only makes updates prompt. */
export const TRACKING_REFETCH_MS = 45_000

/** Public, session-less (G2): the customer needs no account to read these. */
export const orderingKeys = {
  context: (restaurantSlug: string, branchSlug: string, tableCode?: string) =>
    ['public', 'ordering', restaurantSlug, branchSlug, tableCode ?? ''] as const,
  /** Seeded from the placement response so the tracking page opens without a wait. */
  order: (restaurantSlug: string, branchSlug: string, code: string) => ['public', 'order', restaurantSlug, branchSlug, code] as const,
  session: (restaurantSlug: string, branchSlug: string, code: string) => ['public', 'session', restaurantSlug, branchSlug, code] as const,
}

/** The account's own orders (H6): a restaurant session, across every venue. */
export const myOrdersKeys = {
  all: ['my-orders'] as const,
  page: (page: number) => ['my-orders', page] as const,
}

/** Active orders whole + one page of settled ones; polled like the tracking page, no stream spans venues. */
export function myOrdersQuery(page: number) {
  return queryOptions({
    queryKey: myOrdersKeys.page(page),
    queryFn: () => request<MyOrdersResponse>(`/my-orders?page=${page}&per_page=${PAGE_SIZE}`),
    refetchInterval: TRACKING_REFETCH_MS,
  })
}

export function publicPath(restaurantSlug: string, branchSlug: string, suffix = '') {
  return `/public/${encodeURIComponent(restaurantSlug)}/${encodeURIComponent(branchSlug)}${suffix}`
}

/** Branch policy + menu with effective prices (+ the table a dine-in QR named). */
export function orderingContextQuery(restaurantSlug: string, branchSlug: string, tableCode?: string) {
  const query = tableCode ? `?table=${encodeURIComponent(tableCode)}` : ''
  return queryOptions({
    queryKey: orderingKeys.context(restaurantSlug, branchSlug, tableCode),
    queryFn: () => request<OrderingContext>(publicPath(restaurantSlug, branchSlug, `/ordering${query}`)),
  })
}

/** 201 with the new order, or 200 with the first result on a replayed key — the same shape either way. */
export function placeOrder(restaurantSlug: string, branchSlug: string, body: PlaceOrderRequest) {
  return request<PlacedOrder>(publicPath(restaurantSlug, branchSlug, '/orders'), { method: 'POST', body })
}

/**
 * One order by code (G7). `refetchOnMount: 'always'` because the cache may be
 * the placement seed or a read from before a login, and ownership is per caller.
 */
export function orderQuery(restaurantSlug: string, branchSlug: string, code: string) {
  return queryOptions({
    queryKey: orderingKeys.order(restaurantSlug, branchSlug, code),
    queryFn: () => request<TrackedOrder>(publicPath(restaurantSlug, branchSlug, `/${ORDER_SEGMENT}/${encodeURIComponent(code)}`)),
    refetchInterval: TRACKING_REFETCH_MS,
    refetchOnMount: 'always',
  })
}

/** A dine-in session by code (G7): every phone's orders at the table and the bill. */
export function sessionQuery(restaurantSlug: string, branchSlug: string, code: string) {
  return queryOptions({
    queryKey: orderingKeys.session(restaurantSlug, branchSlug, code),
    queryFn: () => request<TrackedSession>(publicPath(restaurantSlug, branchSlug, `/${SESSION_SEGMENT}/${encodeURIComponent(code)}`)),
    refetchInterval: TRACKING_REFETCH_MS,
    refetchOnMount: 'always',
  })
}

/** Customer cancel from SENT/CONFIRMED (T16); `ORDER_NOT_CANCELABLE` names the status it reached instead. */
export function cancelOrder(restaurantSlug: string, branchSlug: string, code: string) {
  return request<TrackedOrder>(publicPath(restaurantSlug, branchSlug, `/${ORDER_SEGMENT}/${encodeURIComponent(code)}/cancel`), {
    method: 'POST',
  })
}

export function takeAwayPath(restaurantSlug: string, branchSlug: string) {
  return `/${restaurantSlug}/${branchSlug}/${TAKE_AWAY_SEGMENT}`
}

export function tablePath(restaurantSlug: string, branchSlug: string, tableCode: string) {
  return `/${restaurantSlug}/${branchSlug}/${TABLE_SEGMENT}/${tableCode}`
}

export function orderTrackingPath(restaurantSlug: string, branchSlug: string, code: string) {
  return `/${restaurantSlug}/${branchSlug}/${ORDER_SEGMENT}/${code}`
}

export function sessionTrackingPath(restaurantSlug: string, branchSlug: string, code: string) {
  return `/${restaurantSlug}/${branchSlug}/${SESSION_SEGMENT}/${code}`
}
